import React, { useEffect, useMemo, useRef } from 'react';
import { BlockNoteSchema, defaultBlockSpecs, defaultStyleSpecs } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { useCreateBlockNote } from '@blocknote/react';
import { useClinicians } from '@features/organizations';
import { useSubjectLanguageVariants } from '@features/user';
import { PlantIcon } from '@jotpsych/ui/icons/PlantIcon';
import { Edit as EditIcon } from '@mui/icons-material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { isEmptyNoteContent, parseNoteToBlocks } from '@utils/blocknote';

import { daysUntil, formatReviewDate } from '../utils/reviewDate';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// Same schema as the editor — no media or code blocks
const {
  codeBlock: _codeBlock,
  audio: _audio,
  video: _video,
  image: _image,
  file: _file,
  ...blockSpecs
} = defaultBlockSpecs;
const { code: _code, ...styleSpecs } = defaultStyleSpecs;
const viewerSchema = BlockNoteSchema.create({ blockSpecs, styleSpecs });

export interface TreatmentPlanData {
  markdown: string;
  treatment_plan_id: string | null;
  /** ISO date (YYYY-MM-DD) the plan must be reviewed by. null when no plan exists. */
  must_be_updated_by?: string | null;
  /** ISO 8601 timestamp of the last write to the plan. */
  updated_at?: string | null;
  /** User ID of the last editor. Caller resolves to a display name. */
  updated_by?: string | null;
}

interface PatientTreatmentPlanCardProps {
  data: TreatmentPlanData | null;
  isLoading?: boolean;
  onEdit?: () => void;
}

export const PatientTreatmentPlanCard: React.FC<PatientTreatmentPlanCardProps> = ({
  data,
  isLoading = false,
  onEdit,
}) => {
  const { singular } = useSubjectLanguageVariants();
  const hasContent = data && !isEmptyNoteContent(data.markdown);
  const editor = useCreateBlockNote({ schema: viewerSchema });
  const loadedRef = useRef<string | null>(null);
  const { clinicians } = useClinicians();

  const updatedByName = useMemo(() => {
    if (!data?.updated_by) return null;
    return clinicians.find(c => c.userId === data.updated_by)?.name ?? null;
  }, [clinicians, data?.updated_by]);

  const reviewDays = daysUntil(data?.must_be_updated_by);
  const reviewLabel = data?.must_be_updated_by
    ? reviewDays !== null && reviewDays < 0
      ? `Overdue · ${formatReviewDate(data.must_be_updated_by)}`
      : `Review by ${formatReviewDate(data.must_be_updated_by)}`
    : null;
  const reviewColor: 'default' | 'warning' | 'error' =
    reviewDays === null
      ? 'default'
      : reviewDays < 0
        ? 'error'
        : reviewDays <= 30
          ? 'warning'
          : 'default';

  // Load content into the read-only editor when data changes
  useEffect(() => {
    if (!hasContent || !data) return;
    if (loadedRef.current === data.markdown) return;
    loadedRef.current = data.markdown;

    void (async () => {
      const blocks = await parseNoteToBlocks(editor, data.markdown);
      if (blocks.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        editor.replaceBlocks(editor.document, blocks);
      }
    })();
  }, [editor, data, hasContent]);

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: { xs: 0, sm: 400 },
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <PlantIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            Treatment Plan
          </Typography>
          {hasContent && (data?.updated_at || updatedByName) && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}
            >
              {data?.updated_at ? `Updated ${formatReviewDate(data.updated_at)}` : null}
              {data?.updated_at && updatedByName ? ' by ' : null}
              {updatedByName ?? null}
            </Typography>
          )}
        </Box>
        {reviewLabel && (
          <Chip
            size="small"
            color={reviewColor === 'default' ? undefined : reviewColor}
            variant={reviewColor === 'default' ? 'outlined' : 'filled'}
            icon={<EventOutlinedIcon sx={{ fontSize: 12 }} />}
            label={reviewLabel}
            sx={{ height: 22, fontSize: '0.6875rem', mr: 0.5 }}
          />
        )}
        {onEdit && (
          <Tooltip title="Edit Treatment Plan">
            <IconButton size="small" onClick={onEdit} sx={{ ml: 'auto' }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent
        onClick={hasContent && onEdit ? onEdit : undefined}
        sx={{
          p: 1.5,
          pt: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          maxHeight: 400,
          ...(hasContent && onEdit ? { cursor: 'pointer' } : {}),
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading treatment plan...
            </Typography>
          </Box>
        ) : !hasContent ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: onEdit ? 1.5 : 0 }}>
              {`No treatment plan for this ${singular}.`}
            </Typography>
            {onEdit && (
              <Button variant="outlined" size="small" onClick={onEdit} sx={{ fontSize: '0.75rem' }}>
                Edit
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'backgroundColors.tertiary',
              border: '1px solid',
              borderColor: 'borderColors.lightprimary',
            }}
          >
            <BlockNoteView
              editor={editor}
              editable={false}
              theme="light"
              formattingToolbar={false}
              sideMenu={false}
              slashMenu={false}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
